/**
 * Shared mapping from recent workflow runs to {@link StatusTimelineEntry}[] for
 * the "last N runs" strip. Used by both the flows table (FlowList) and the
 * suite builder cards so the strip renders identically in both places.
 */
import { RunSummaryTooltip, type StatusTimelineEntry, type StatusTone } from '@chatsdet/ui';
import type { FailureDiagnosticCategory } from '@chatsdet/types/issues';
import type { WorkflowRunDto } from '../../ui/pages/flow-list/types';
import {
  formatTooltipTimestamp,
  formatWorkflowRunStatus,
  getWorkflowRunTimestamp,
  workflowRunStatusToTestStatus,
  workflowRunStatusTone,
} from '../../ui/pages/flow-list/utils';

/** Minimal run shape the strip needs — structurally compatible with WorkflowRunDto. */
export type TimelineRun = {
  id: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  errorMessage: string | null;
  diagnosticStatus?: string | null;
  diagnosticCategory?: FailureDiagnosticCategory | null;
};

/**
 * Map up-to-10 newest-first runs to timeline entries. Mirrors FlowList's prior
 * inline mapping; defensive against status values outside the known union so a
 * new DB enum value never crashes the strip.
 */
export function buildRunTimelineEntries(
  runs: TimelineRun[],
  workflowName: string
): StatusTimelineEntry[] {
  return runs.slice(0, 10).map((run) => {
    const status = run.status as WorkflowRunDto['status'];
    const statusLabel = formatWorkflowRunStatus(status);
    const timestampLabel = formatTooltipTimestamp(getWorkflowRunTimestamp(run as WorkflowRunDto));
    const durationMs =
      run.startedAt && run.finishedAt
        ? new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
        : null;
    const tone: StatusTone = workflowRunStatusTone[status] ?? 'muted';
    return {
      id: run.id,
      tone,
      label: statusLabel,
      tooltip: `${statusLabel} • ${timestampLabel}`,
      tooltipContent: (
        <RunSummaryTooltip
          name={workflowName}
          status={workflowRunStatusToTestStatus[status] ?? 'queued'}
          startedAt={run.startedAt}
          finishedAt={run.finishedAt}
          durationMs={durationMs}
          errorMessage={run.errorMessage}
        />
      ),
    };
  });
}

/**
 * Statuses the backend's health query treats as ending the streak — see
 * `listTerminalRunStatusesForHealth` in
 * `packages/db/src/repositories/workflow-runs.repo.ts`, which filters to
 * `status IN ('success','failed','flaky_success','timeout')` before
 * `classifyHealth`'s trailing-failure walk runs, and breaks that walk on a
 * pass. `flaky_success` isn't a possible `workflow_runs.status` value on this
 * DTO (it's a `workflow_test_runs`-level outcome), so only `success` applies.
 */
const STREAK_BREAKING_STATUSES: ReadonlySet<WorkflowRunDto['status']> = new Set(['success']);

/**
 * Statuses the backend's health query excludes entirely from the trailing-
 * failure walk — they're skipped over transparently, neither ending the
 * streak nor counting as a diagnosable failure within it. Everything else not
 * in {@link STREAK_BREAKING_STATUSES} (i.e. `failed`/`timeout`) extends it.
 */
const STREAK_SKIPPED_STATUSES: ReadonlySet<WorkflowRunDto['status']> = new Set([
  'queued',
  'running',
  'inconclusive',
  'canceled',
]);

/**
 * Find the category of the most recent run — within the *current failure
 * streak* — that has a completed failure diagnosis. Runs must be ordered
 * newest-first (matching {@link buildRunTimelineEntries}'s input).
 *
 * Mirrors the backend's `classifyHealth` trailing-failure walk (see
 * `apps/api/src/workflows/helpers/workflow-health.ts` and
 * `listTerminalRunStatusesForHealth`): walk from the newest run backwards,
 * skipping over non-terminal/excluded statuses without ending the streak,
 * extending through failures, and stopping only at a `success`. Within that
 * walked prefix, return the category of the newest run with
 * `diagnosticStatus === 'completed'` — since we walk newest-first and return
 * on the first match, that's automatically the most recent diagnosed run in
 * the streak. Returns null if the streak is empty or nothing in it has a
 * completed diagnosis.
 */
export function findLastDiagnosedCategoryInStreak(
  runs: readonly TimelineRun[]
): FailureDiagnosticCategory | null {
  for (const run of runs) {
    const status = run.status as WorkflowRunDto['status'];
    if (STREAK_BREAKING_STATUSES.has(status)) break;
    if (STREAK_SKIPPED_STATUSES.has(status)) continue;
    if (run.diagnosticStatus === 'completed' && run.diagnosticCategory) {
      return run.diagnosticCategory;
    }
  }
  return null;
}
